import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Switch,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import {
  BoltIcon,
  PlusIcon,
  TrashIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useTheme } from '../../context/ThemeContext';
import { financeApi } from '../../api/financeApi';
import AppIconButton from '../ui/AppIconButton';
import { PrimaryButton, SecondaryButton, EmptyState } from '../ui';

// --- Types ---

interface TransactionRule {
  id: number;
  pattern: string;
  mappedType: string;
  mappedCategoryId: number | null;
  mappedCategoryName: string | null;
  mappedIncomeCategory: string | null;
  askEveryTime: boolean;
}

interface CategoryItem {
  id: number;
  name: string;
  type: string;
  isSystem?: boolean;
}

interface TransactionRulesManagerProps {
  embedded?: boolean;
}

const FALLBACK_INCOME_CATEGORIES = [
  { value: 'DAILY_SALES', label: 'Daily Sales' },
  { value: 'CATERING', label: 'Catering' },
  { value: 'EVENTS', label: 'Events' },
  { value: 'TIPS', label: 'Tips' },
  { value: 'RENTAL', label: 'Rental Income' },
  { value: 'DELIVERY_COMMISSION', label: 'Delivery Commission' },
  { value: 'OTHER', label: 'Other' },
];

// --- Component ---

const TransactionRulesManager: React.FC<TransactionRulesManagerProps> = ({ embedded = false }) => {
  const { theme } = useTheme();

  // Data
  const [rules, setRules] = useState<TransactionRule[]>([]);
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [incomeCategories, setIncomeCategories] = useState<Array<{ value: string; label: string }>>([]);
  const [loading, setLoading] = useState(true);

  // Add rule form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newRule, setNewRule] = useState({
    pattern: '',
    mappedType: 'EXPENSE' as 'EXPENSE' | 'INCOME',
    mappedCategoryId: '' as number | '',
    mappedIncomeCategory: '',
    askEveryTime: false,
  });
  const [savingRule, setSavingRule] = useState(false);

  // Test rule
  const [testInput, setTestInput] = useState('');
  const [testResult, setTestResult] = useState<{ matched: boolean; message: string } | null>(null);
  const testTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- Fetch ---

  const fetchRules = useCallback(async () => {
    try {
      const res = await financeApi.getTransactionRules();
      setRules(Array.isArray(res.data) ? res.data : []);
    } catch (err: unknown) {
      console.error('Error fetching transaction rules:', err);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await financeApi.getCategories();
      const data = Array.isArray(res.data) ? res.data : res.data?.content || [];
      setCategories(data);
    } catch (err: unknown) {
      console.error('Error fetching categories:', err);
    }
  }, []);

  const fetchIncomeCategories = useCallback(async () => {
    try {
      const res = await financeApi.getIncomeCategories();
      const data = Array.isArray(res.data) ? res.data : [];
      setIncomeCategories(data.map((c: { name: string; displayName?: string }) => ({
        value: c.name,
        label: c.displayName || c.name,
      })));
    } catch {
      setIncomeCategories(FALLBACK_INCOME_CATEGORIES);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchRules(), fetchCategories(), fetchIncomeCategories()]);
      setLoading(false);
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Test rule debounced ---

  const handleTestInput = (value: string) => {
    setTestInput(value);
    if (testTimerRef.current) clearTimeout(testTimerRef.current);

    if (!value.trim()) {
      setTestResult(null);
      return;
    }

    testTimerRef.current = setTimeout(() => {
      const upperVal = value.toUpperCase();
      const match = rules.find((r) => upperVal.includes(r.pattern.toUpperCase()));
      if (match) {
        const categoryLabel = match.mappedCategoryName || match.mappedIncomeCategory || 'Unknown';
        setTestResult({
          matched: true,
          message: `Matches rule "${match.pattern}" -> ${categoryLabel}`,
        });
      } else {
        setTestResult({ matched: false, message: 'No rule matches' });
      }
    }, 300);
  };

  // --- Handlers ---

  const handleAddRule = async () => {
    if (!newRule.pattern.trim() || newRule.pattern.trim().length < 3) {
      toast.error('Pattern must be at least 3 characters');
      return;
    }
    if (newRule.mappedType === 'EXPENSE' && !newRule.mappedCategoryId) {
      toast.error('Category is required');
      return;
    }
    if (newRule.mappedType === 'INCOME' && !newRule.mappedIncomeCategory) {
      toast.error('Income category is required');
      return;
    }

    setSavingRule(true);
    try {
      const payload: Record<string, unknown> = {
        pattern: newRule.pattern.trim(),
        mappedType: newRule.mappedType,
        askEveryTime: newRule.askEveryTime,
      };
      if (newRule.mappedType === 'EXPENSE') {
        payload.mappedCategoryId = newRule.mappedCategoryId;
      } else {
        payload.mappedIncomeCategory = newRule.mappedIncomeCategory;
      }
      await financeApi.createTransactionRule(payload);
      toast.success('Rule created');
      setNewRule({ pattern: '', mappedType: 'EXPENSE', mappedCategoryId: '', mappedIncomeCategory: '', askEveryTime: false });
      setShowAddForm(false);
      await fetchRules();
    } catch (err: unknown) {
      console.error('Error creating rule:', err);
      toast.error('Failed to create rule');
    } finally {
      setSavingRule(false);
    }
  };

  const handleDeleteRule = async (id: number) => {
    try {
      await financeApi.deleteTransactionRule(id);
      toast.success('Rule deleted');
      setRules((prev) => prev.filter((r) => r.id !== id));
    } catch (err: unknown) {
      console.error('Error deleting rule:', err);
      toast.error('Failed to delete rule');
    }
  };

  // --- Expense category options for searchable dropdown ---

  const expenseCategories = useMemo(() => categories.filter((c) => c.type !== 'INCOME'), [categories]);

  // --- Shared styles ---

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.components?.input?.background || '#FFFFFF',
      borderRadius: '10px',
      '& fieldset': { borderColor: theme.components?.input?.border || '#DDD8D2' },
      '&:hover fieldset': { borderColor: theme.primary },
      '&.Mui-focused fieldset': { borderColor: theme.components?.input?.focus || theme.primary },
    },
    '& .MuiInputLabel-root': { color: theme.gray?.[500] || '#666' },
    '& .MuiInputLabel-root.Mui-focused': { color: theme.primary },
    '& .MuiInputBase-input': { color: theme.text },
  };

  const selectSx = {
    borderRadius: '10px',
    backgroundColor: theme.components?.input?.background || '#FFFFFF',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.components?.input?.border || '#DDD8D2' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.primary },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.components?.input?.focus || theme.primary },
    '& .MuiSelect-select': { color: theme.text },
  };

  // --- Loading ---

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} sx={{ color: theme.primary }} />
      </Box>
    );
  }

  // --- Content ---

  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Section title (only when embedded) */}
      {embedded && (
        <Box>
          <Typography
            sx={{ fontWeight: 700, color: theme.text, fontSize: { xs: '1rem', sm: '1.1rem' }, lineHeight: 1.3 }}
          >
            Transaction Rules
          </Typography>
          <Typography variant="body2" sx={{ color: theme.gray?.[500] || '#666', mt: 0.25, fontSize: '0.813rem' }}>
            Auto-categorize bank statement imports based on description patterns
          </Typography>
        </Box>
      )}

      {/* 1. Add Rule Form (collapsible) */}
      <Box>
        {showAddForm ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              p: 2,
              borderRadius: '10px',
              border: `1px dashed ${theme.gray?.[300] || '#C8C5BD'}`,
              backgroundColor: theme.button?.secondary?.bg || theme.background,
            }}
          >
            <TextField
              size="small"
              placeholder="e.g., RAPYAL MEAT"
              label="Pattern"
              value={newRule.pattern}
              onChange={(e) => setNewRule((prev) => ({ ...prev, pattern: e.target.value }))}
              autoFocus
              sx={inputSx}
              helperText={newRule.pattern.trim().length > 0 && newRule.pattern.trim().length < 3 ? 'Min 3 characters' : ''}
              error={newRule.pattern.trim().length > 0 && newRule.pattern.trim().length < 3}
            />

            {/* Type chips */}
            <Box>
              <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontWeight: 600, mb: 0.75, display: 'block', ml: 0.5, fontSize: '0.75rem' }}>
                Type
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75 }}>
                {(['EXPENSE', 'INCOME'] as const).map((type) => {
                  const isActive = newRule.mappedType === type;
                  const chipColor = type === 'EXPENSE'
                    ? (theme.danger || '#D99982')
                    : (theme.success || '#4CAF50');
                  return (
                    <Chip
                      key={type}
                      label={type === 'EXPENSE' ? 'Expense' : 'Income'}
                      onClick={() => setNewRule((prev) => ({
                        ...prev,
                        mappedType: type,
                        mappedCategoryId: '',
                        mappedIncomeCategory: '',
                      }))}
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.813rem',
                        minHeight: 34,
                        cursor: 'pointer',
                        backgroundColor: isActive ? `${chipColor}20` : theme.gray?.[100] || '#F0F0F0',
                        color: isActive ? chipColor : theme.gray?.[500] || '#666',
                        border: isActive ? `1.5px solid ${chipColor}` : `1px solid ${theme.gray?.[200] || '#E0E0E0'}`,
                        '&:hover': { opacity: 0.85 },
                      }}
                    />
                  );
                })}
              </Box>
            </Box>

            {/* Category dropdown */}
            {newRule.mappedType === 'EXPENSE' ? (
              <Autocomplete
                size="small"
                options={expenseCategories}
                getOptionLabel={(opt) => opt.name}
                value={expenseCategories.find((c) => c.id === newRule.mappedCategoryId) || null}
                onChange={(_, val) => setNewRule((prev) => ({ ...prev, mappedCategoryId: val?.id || '' }))}
                renderInput={(params) => (
                  <TextField {...params} label="Expense Category" placeholder="Search categories..." sx={inputSx} />
                )}
                sx={{ '& .MuiAutocomplete-popupIndicator': { color: theme.gray?.[400] || '#999' } }}
              />
            ) : (
              <FormControl size="small" fullWidth>
                <InputLabel sx={{ color: theme.gray?.[500] || '#666', '&.Mui-focused': { color: theme.primary } }}>
                  Income Category
                </InputLabel>
                <Select
                  value={newRule.mappedIncomeCategory}
                  label="Income Category"
                  onChange={(e) => setNewRule((prev) => ({ ...prev, mappedIncomeCategory: e.target.value }))}
                  sx={selectSx}
                >
                  {(incomeCategories.length > 0 ? incomeCategories : FALLBACK_INCOME_CATEGORIES).map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Ask every time toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch
                size="small"
                checked={newRule.askEveryTime}
                onChange={(e) => setNewRule((prev) => ({ ...prev, askEveryTime: e.target.checked }))}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: theme.primary },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: theme.primary },
                }}
              />
              <Typography variant="body2" sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.813rem' }}>
                Ask every time (show as suggested, not auto-assigned)
              </Typography>
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <SecondaryButton
                size="small"
                onClick={() => {
                  setShowAddForm(false);
                  setNewRule({ pattern: '', mappedType: 'EXPENSE', mappedCategoryId: '', mappedIncomeCategory: '', askEveryTime: false });
                }}
                sx={{ borderRadius: '10px', minHeight: 36 }}
              >
                Cancel
              </SecondaryButton>
              <PrimaryButton
                size="small"
                onClick={handleAddRule}
                disabled={savingRule || newRule.pattern.trim().length < 3}
                loading={savingRule}
                startIcon={!savingRule ? <CheckIcon style={{ width: 16, height: 16 }} /> : undefined}
                sx={{ borderRadius: '10px', minHeight: 36 }}
              >
                Add Rule
              </PrimaryButton>
            </Box>
          </Box>
        ) : (
          <SecondaryButton
            size="small"
            startIcon={<PlusIcon style={{ width: 16, height: 16 }} />}
            onClick={() => setShowAddForm(true)}
            sx={{ fontSize: '0.813rem' }}
          >
            Add Rule
          </SecondaryButton>
        )}
      </Box>

      {/* 2. Test Rule */}
      <Box>
        <TextField
          size="small"
          fullWidth
          placeholder="Test: type a transaction description..."
          value={testInput}
          onChange={(e) => handleTestInput(e.target.value)}
          sx={inputSx}
          inputProps={{ style: { fontSize: '0.875rem', padding: '8px 12px' } }}
        />
        {testResult && (
          <Typography
            variant="body2"
            sx={{
              mt: 0.75,
              ml: 0.5,
              fontSize: '0.813rem',
              fontWeight: 500,
              color: testResult.matched ? (theme.success || '#4CAF50') : (theme.gray?.[400] || '#999'),
            }}
          >
            {testResult.message}
          </Typography>
        )}
      </Box>

      {/* 3. Rules list */}
      {rules.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {rules.map((rule) => (
            <Box
              key={rule.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1,
                borderRadius: '10px',
                backgroundColor: theme.button?.secondary?.bg || theme.background,
                border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
              }}
            >
              {/* Left content — wraps internally on narrow width without pushing trash off the row */}
              <Box
                sx={{
                  flex: '1 1 0',
                  minWidth: 0,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.text,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    flex: '1 1 auto',
                    minWidth: 120,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={rule.pattern}
                >
                  &ldquo;{rule.pattern}&rdquo;
                </Typography>
                <Box
                  sx={{
                    flexShrink: 0,
                    px: 1,
                    py: 0.25,
                    borderRadius: '6px',
                    backgroundColor: rule.mappedType === 'EXPENSE'
                      ? ((theme.danger || '#D99982') + '18')
                      : ((theme.success || '#4CAF50') + '18'),
                    color: rule.mappedType === 'EXPENSE'
                      ? (theme.danger || '#D99982')
                      : (theme.success || '#4CAF50'),
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                  }}
                >
                  {rule.mappedType}
                </Box>
                <Chip
                  label={rule.mappedCategoryName || rule.mappedIncomeCategory || 'Uncategorized'}
                  size="small"
                  sx={{
                    flexShrink: 0,
                    fontSize: '0.65rem',
                    height: 22,
                    fontWeight: 600,
                    backgroundColor: theme.button.primary.bg,
                    color: theme.button.primary.text,
                  }}
                />
                {rule.askEveryTime && (
                  <Box
                    sx={{
                      flexShrink: 0,
                      px: 0.75,
                      py: 0.25,
                      borderRadius: '6px',
                      backgroundColor: (theme.warning || '#FFC107') + '20',
                      color: theme.warning || '#FFC107',
                      fontSize: '0.65rem',
                      fontWeight: 600,
                    }}
                  >
                    Ask Every Time
                  </Box>
                )}
              </Box>
              <AppIconButton
                icon={<TrashIcon style={{ width: 16, height: 16 }} />}
                tooltip="Delete rule"
                variant="ghost"
                size="small"
                onClick={() => handleDeleteRule(rule.id)}
                sx={{ flexShrink: 0, '&:hover': { backgroundColor: 'var(--status-bad-bg)', color: 'var(--status-bad)' } }}
                data-testid={`delete-rule-${rule.id}`}
              />
            </Box>
          ))}
        </Box>
      ) : (
        <EmptyState
          icon={<BoltIcon style={{ width: 44, height: 44, color: 'var(--text-tertiary)' }} />}
          title="No rules yet"
          subtitle='Rules auto-categorize imported bank transactions. Tick "Save Rule" during a bank import, or add one manually above.'
          data-testid="rules-empty-state"
        />
      )}
    </Box>
  );

  // When embedded (in Settings), no outer Paper wrapper
  if (embedded) {
    return content;
  }

  // Standalone (in BankImport modal), also no Paper since Modal provides it
  return content;
};

export default TransactionRulesManager;
